import { useEffect, useMemo, useState } from "react";
import type {
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { createIsPlacementShown } from "@design-system/Board/boardVisibility";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    getLiveMovedCardIds,
    getWorkBoardSnapshot,
    isSameWorkBoardSnapshot,
    NO_IDS,
    type WorkBoardScope,
    type WorkBoardSnapshot,
} from "./liveMovedCards";

interface WorkBoardCards {
    rows: readonly ClientsWorkOverviewWorkRow[];
    getPlacements: (row: ClientsWorkOverviewWorkRow) => BoardPlacement[];
    columns: readonly BoardColumnDef[];
    lanes: readonly BoardLaneDef[] | undefined;
    collapsedColumnIds: readonly string[];
    collapsedLaneIds: readonly string[];
}

/**
 * The board's `enteringItemIds` for cards a live update (another user moving
 * a stage, say) moved to another column or lane, set in the render the new
 * data lands in so their new cards enter. Off Board or while loading
 * (`cards` null) there's no baseline, so the next load doesn't animate.
 * Cards landing in a collapsed column or lane are left out. The board
 * enters them in place without scrolling, so one outside a long column's
 * window doesn't animate. The ids clear once the new cards have mounted,
 * so a card that moves again enters again.
 */
export const useLiveMovedCardIds = ({
    scope,
    cards,
}: {
    scope: WorkBoardScope;
    cards: WorkBoardCards | null;
}) => {
    const rows = cards?.rows;
    const getPlacements = cards?.getPlacements;
    const snapshot = useMemo(
        () =>
            rows === undefined || getPlacements === undefined
                ? null
                : getWorkBoardSnapshot({ scope, rows, getPlacements }),
        [scope, rows, getPlacements],
    );
    const [baseline, setBaseline] = useState<WorkBoardSnapshot | null>(null);
    const [enteringItemIds, setEnteringItemIds] = useState(NO_IDS);

    // By content: React Compiler rebuilds `snapshot` every render.
    if (!isSameWorkBoardSnapshot(baseline, snapshot)) {
        setBaseline(snapshot);
        const isPlacementShown = cards && createIsPlacementShown(cards);
        const movedIds =
            snapshot === null || isPlacementShown === null
                ? NO_IDS
                : getLiveMovedCardIds(baseline, snapshot).filter((rowId) =>
                      snapshot.placementsByRowId
                          .get(rowId)
                          ?.some(isPlacementShown),
                  );
        if (movedIds.length > 0 || snapshot?.scopeKey !== baseline?.scopeKey) {
            setEnteringItemIds(movedIds.length === 0 ? NO_IDS : movedIds);
        }
    }

    useEffect(() => {
        if (enteringItemIds.length > 0) {
            setEnteringItemIds(NO_IDS);
        }
    }, [enteringItemIds]);

    return enteringItemIds;
};
